import {DataStatus, openDialog, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {useLayoutEffect, useMemo, useRef, useState} from 'react';

import {getSafely, isNotNilOrEmpty, suffixTestId} from 'shared/web';

import {ActiveFilterBar} from './components/ActiveFilterBar/ActiveFilterBar';
import {AggregationStatusBar} from './components/AggregationStatusBar/AggregationStatusBar';
import {BulkActionSection} from './components/BulkActionSection/BulkActionSection';
import {ConditionalDndWrapper} from './components/ConditionalDndWrapper/ConditionalDndWrapper';
import {DefaultEmptyStateRenderer} from './components/DefaultEmptyStateRenderer/DefaultEmptyStateRenderer';
import {ExternalFiltersPortal} from './components/ExternalFiltersPortal/ExternalFiltersPortal';
import {GridBulkOperations} from './components/GridBulkOperations/GridBulkOperations';
import {GridHeader} from './components/GridHeader/GridHeader';
import {EditPresetDialog} from './components/PresetPanel/components/EditPresetDialog/EditPresetDialog';
import {Sidebar} from './components/Sidebar/Sidebar';
import {TableScrollArea} from './components/TableScrollArea/TableScrollArea';
import {type ColumnMenuContextValue} from './context/ColumnMenuContext';
import {useDataGridContext} from './context/useDataGridContext';
import {useColumnAutosize} from './hooks/useColumnAutosize';
import {useColumnLayout} from './hooks/useColumnLayout';
import {useColumnResize} from './hooks/useColumnResize';
import {useColumnSelection} from './hooks/useColumnSelection';
import {useContainerHeight} from './hooks/useContainerHeight';
import {useDragOverlay} from './hooks/useDragOverlay';
import {useEagGridHandlers} from './hooks/useEagGridHandlers';
import {useExternalBulkActionInvocation} from './hooks/useExternalBulkActionInvocation';
import {useGridActionColumn} from './hooks/useGridActionColumn';
import {useGridDataNotification} from './hooks/useGridDataNotification';
import {useGridDragAndDrop} from './hooks/useGridDragAndDrop';
import {useGridSelection} from './hooks/useGridSelection';
import {useGridStateSync} from './hooks/useGridStateSync';
import {useGroupRowModel} from './hooks/useGroupRowModel';
import {useHandleLink} from './hooks/useHandleLink';
import {useIsInlineDesktop} from './hooks/useIsInlineDesktop';
import {useIsMobileView} from './hooks/useIsMobileView';
import {usePresetState} from './hooks/usePresetState';
import {useResolveSuggestionFilterLabels} from './hooks/useResolveSuggestionFilterLabels';
import {useRowActionClick} from './hooks/useRowActionClick';
import {useRowClickFallback} from './hooks/useRowClickFallback';
import {useRowContextMenu} from './hooks/useRowContextMenu';
import {
  selectActiveDragId,
  selectColumnGroupsVisible,
  selectDataQuery,
  selectColumnStates,
} from './store/createEagGridStore';
import {
  useColumnMovingAllowed,
  useActionColumnEnabled,
  usePaginationEnabled,
  useColumnMenuEnabled,
  useColumnPinningAllowed,
  useColumnAutoSizingAllowed,
  useActivePanel,
  useFilterValues,
  usePresetsEnabled,
  usePresetDisplayMode,
  useGridIsLoading,
  useGridHasFetchError,
  useGridPage,
  useGridPreviousRows,
  useHasSilentRefetching,
  useGridRows,
  useGridFetchState,
  useGridTotalCount,
  useSelectedRowIds,
  useActiveFilterCount,
  useGridSettingsSafe,
  useIsColumnsReady,
  useClipboardExportAllowed,
  useContextMenuEnabled,
  useSummaryRowEnabled,
  useStatusBarVisible,
  useRangeSelectAllowed,
} from './store/selectors';
import {useGridHasBulkActions} from './store/selectors/primitives';
import {useActionsDefinition} from './store/selectors/useActionsDefinition';
import {selectRowSelectMode} from './store/selectors/useBehavior';
import {useEagGridSelector} from './store/useEagGridSelector';
import {useEagGridStore} from './store/useEagGridStore';
import type {ActionDefinitionObject} from './types/ActionDefinition';
import type {EagGridTableProps} from './types/EagGridTableProps';
import type {RowData} from './types/RowData';
import {computeSeededColumnStates} from './utils/computeSeededColumnStates';
import {getColumnData} from './utils/getColumnData';
import {getPresetStates} from './utils/getPresetStates';
import {HeaderHeightToPx, LineHeightToPx} from './utils/lineHeightToValue';
import {setSourceSearchParamsByGridCode} from './utils/url/setSourceSearchParamsByGridCode';

/** Below this height (px) the inline sidebar switches to a fixed drawer. */
const COMPACT_SIDEBAR_THRESHOLD = 300;

/** Stable empty-rows reference for the fetch-error state (avoids a fresh [] per render). */
const NO_ROWS: RowData[] = [];

/**
 * Stable fallback for the definition-load window, when useActionsDefinition() is
 * still null. A fresh `{}` per render would change useGridDataNotification's
 * actionsDefinition dependency on every render, re-firing its effect and looping
 * host -> setGridData -> re-render -> new `{}` -> effect (T20-135733 class of bug).
 */
const EMPTY_ACTIONS_DEFINITION: ActionDefinitionObject = {};

/**
 * Core grid component that owns the table layout, header, sidebar, and data
 * fetching lifecycle for a single EagGrid instance.
 *
 * ## State management — why no prop drilling
 *
 * EagGridTable deliberately does **not** accept or forward low-level state slices
 * (loading, rows, column widths, drag state, etc.) as props.  Instead it reads
 * and mutates state through `useEagGridStore()` / `useEagGridSelector()`, which
 * reach the store provided by the parent `EagGridStoreProvider`.
 *
 * This keeps the component surface small and prevents prop explosion as new UI
 * features are added.  Specifically for the filter system redesign:
 *
 * - `filterValues` flows from the store into `handleFilterChange`
 *   and `handleResetFilters`, then gets passed to the sidebar as callbacks —
 *   no intermediate component re-render required.
 * - `activePanel` lives in the store; GridHeader toggles it, Sidebar
 *   reads it, and the new FilterPanel close button dispatches `setActivePanel(null)`
 *   — all without EagGridTable acting as a message bus.
 * - Drag state (`activeDragId`, `overColumnId`, `dropSide`) is written here and
 *   read directly by `HeaderCell` / `GridCell` 4 levels down — zero
 *   intermediary props.
 *
 * ## Column config stability
 *
 * `columnConfig` is treated as stable after mount.  Dynamic column changes require
 * remounting the component (pass a new `key` prop to the parent `<EagGrid>`).
 * This is enforced by the empty-dep `useLayoutEffect` that initialises column states.
 *
 * ## Filter panel integration
 *
 * `handleFilterChange` and `handleResetFilters` write directly to the store via
 * `store.setFilterValues`. The sidebar `FilterPanel` receives these callbacks
 * without any context subscription re-renders.
 */
// eslint-disable-next-line max-lines-per-function, max-statements -- Pre-existing violation
export function EagGridTable(props: EagGridTableProps) {
  /** Extract props to local bindings for shorter names in JSX. */
  const autoHeight = props.autoHeight;
  const gridCode = props.gridCode;
  const columnConfig = props.columnConfig;
  const definition = props.definition;
  const emptyStateNode = <DefaultEmptyStateRenderer emptyStatus={props.emptyState} />;
  const actionsDefinition = useActionsDefinition();
  const onRowSelectionChange = props.onRowSelectionChange;
  const testId = props['data-testid'];
  const actionCallback = props.actionCallback;

  const gridContext = useDataGridContext();
  const store = useEagGridStore();
  const hasBulkActions = useGridHasBulkActions();

  // Filter state from store — single source of truth (replaces DatagridFiltersModelProvider context).
  const filterValues = useFilterValues();
  // Resolves async (MULTI_CHOICE_ASYNC) filter labels so the active-filter bar shows a
  // human name instead of the raw stored key; returns the shared suggestion-label cache.
  const suggestionLabels = useResolveSuggestionFilterLabels(columnConfig);
  const activeFilterCount = useActiveFilterCount();
  const columnStatesForSync = useEagGridSelector(selectColumnStates);

  useGridStateSync({
    filterValues,
    columnStates: columnStatesForSync,
    onGridStateChange: gridContext.onGridStateChange,
  });

  // Behavior flags from store
  const columnMovingAllowed = useColumnMovingAllowed();
  const actionColumnEnabled = useActionColumnEnabled();
  const paginationEnabled = usePaginationEnabled();
  const columnMenuEnabled = useColumnMenuEnabled();
  const columnPinningAllowed = useColumnPinningAllowed();
  const columnAutoSizingAllowed = useColumnAutoSizingAllowed();

  // Tracks the dataQuery seen by the last column-state seed, so the effect below can
  // tell a preset switch/load (dataQuery changes) from an in-place activePreset update
  // such as a preset Save (dataQuery unchanged). Written only inside the layout effect.
  const seededDataQueryRef = useRef<ReturnType<typeof selectDataQuery>>(null);

  // columnConfig is treated as stable after mount; it only recomputes when the
  // active preset or its dataQuery changes (preset switch/load, or an in-place Save).
  // useLayoutEffect runs synchronously before paint so the first visible render
  // already has column states — no flicker. It must NOT be done during render
  // because store.setState notifies useSyncExternalStore subscribers in other
  // components, which triggers the "update while rendering" React warning.
  useLayoutEffect(() => {
    const currentDataQuery = selectDataQuery(store.getState());

    // A columnConfig recompute with an UNCHANGED dataQuery is an in-place activePreset
    // update (e.g. a preset Save), not a preset switch/load — so preserve the live sort.
    // See computeSeededColumnStates for the full rationale.
    const isInPlaceUpdate = seededDataQueryRef.current === currentDataQuery;
    seededDataQueryRef.current = currentDataQuery;
    const liveColumnStates = isInPlaceUpdate ? store.getState().columnStates : null;

    store.setColumnStates(computeSeededColumnStates(columnConfig, liveColumnStates));
    // store is ref-stable; columnConfig recomputes on activePreset/dataQuery change.
  }, [columnConfig, store]);

  const dataQuery = useEagGridSelector(selectDataQuery)!;

  const selectedRowIds = useSelectedRowIds();
  // rowSelectMode is a string primitive — safe with Object.is() comparison, no referential stability concern.
  const rowSelectMode = useEagGridSelector(selectRowSelectMode);
  // hasCheckboxColumn is derived from rowSelectMode — no additional subscription needed.
  const hasCheckboxColumn = rowSelectMode !== 'NONE';
  const page = useGridPage();
  // False until the first applyPresetColumnsAndSort call resolves column widths from the preset.
  // Suppresses the header+column skeleton to prevent width-jump flash on initial load.
  const isColumnsReady = useIsColumnsReady();

  const gridSettings = useGridSettingsSafe();
  const itemsPerPage = gridSettings.itemsPerPage;
  const summaryRowEnabled = useSummaryRowEnabled();
  const showSummaryRow = summaryRowEnabled && gridSettings.summaryRowVisible;
  const statusBarVisible = useStatusBarVisible();
  const rangeSelectAllowed = useRangeSelectAllowed();
  const showStatusBar = statusBarVisible && rangeSelectAllowed;
  const isMobile = useIsMobileView();
  const isInlineDesktop = useIsInlineDesktop();

  const rows = useGridRows();
  const fetchState = useGridFetchState();
  const previousRows = useGridPreviousRows();
  const hasSilentRefetching = useHasSilentRefetching();
  const isLoading = useGridIsLoading();
  const isErrorState = useGridHasFetchError();
  const isEmptyState = rows.length === 0 && !isLoading && isColumnsReady && !isErrorState;
  // A settled fetch error keeps the last successful rows in the store (so retry can
  // recover them silently), but they must never render: the non-autoHeight error
  // surface only hides them behind an opaque overlay, and the autoHeight in-flow
  // surface has no overlay at all — stale rows would stay visible and clickable
  // with the error box merely stacked underneath.
  const tableRows = isErrorState ? NO_ROWS : rows;

  const totalCount = useGridTotalCount();

  // useGridActionColumn must precede useColumnLayout: the layout's pinnedRightOffsets
  // accumulates leftward from the action column's left edge.
  const {actionColumnWidth} = useGridActionColumn({
    rows,
    previousRows,
    actionsDefinition,
    gridSettings,
    actionColumnEnabled,
    isMobile,
  });

  // All column-layout geometry: flattened config, ordered/visible/pinned partitions,
  // O(1) lookup Maps, sticky offsets, sort model, pinning edge keys.
  const layout = useColumnLayout({
    columnConfig,
    hasCheckboxColumn,
    actionColumnWidth,
  });

  const colgroupRef = useRef<HTMLTableColElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const resizeManager = useColumnResize(colgroupRef, layout.getWidth);
  const handleLink = useHandleLink();

  const sortableIds = useMemo(
    () => layout.visibleColumnStates.map((cs) => cs.key),
    [layout.visibleColumnStates]
  );

  const columnGroupsVisible = useEagGridSelector(selectColumnGroupsVisible);

  // Live-order group-header-row model — shared by GroupHeaderRow (rendering)
  // and the drag handlers (group-header drag routing).
  const groupRowModel = useGroupRowModel({
    columnConfig,
    nonRightPinnedColumns: layout.nonRightPinnedColumns,
    rightPinnedColumns: layout.rightPinnedColumns,
    columnGroupsVisible,
  });

  const dnd = useGridDragAndDrop({
    visibleColumnStates: layout.visibleColumnStates,
    store,
    reorderByKey: store.reorderColumnsByKey,
    groupRowModel,
  });

  const activeDragId = useEagGridSelector(selectActiveDragId);
  const activePanel = useActivePanel();

  const {containerHeight, containerRef} = useContainerHeight();
  const isCompactSidebar =
    (props.drawerModeEnabled ?? false) ||
    (containerHeight > 0 && containerHeight < COMPACT_SIDEBAR_THRESHOLD);

  // Container element as STATE (not a ref) — it feeds ColumnMenuContext.menuBoundary,
  // which header menu Dropdowns consume during render as their floating-ui boundary.
  const [menuBoundary, setMenuBoundary] = useState<HTMLElement | null>(null);

  const {
    handleTogglePanel,
    handleClosePanel,
    handleTableAreaClick,
    handleFilterChange,
    handleResetFilters,
    handlePinColumn,
    handleSortDirect,
    handleSort,
    handlePageChange,
  } = useEagGridHandlers({store, filterValues});

  const {autosizeColumn: handleAutosizeColumn, autosizeAllColumns: handleAutosizeAllColumns} =
    useColumnAutosize(tableRef, layout.flatColumnMap, columnMenuEnabled);

  const t = useTranslationContext();
  const presetState = usePresetState();
  const presetsEnabled = usePresetsEnabled();
  const presetDisplayMode = usePresetDisplayMode();

  // "Save as view" in the active-filter bar (mock .filtbar__save) opens the same
  // create-preset dialog as the preset strip's "+" — it captures the current
  // columns + settings as a new named view. Only offered when presets are enabled.
  const handleSaveAsView = () =>
    openDialog(
      <EditPresetDialog
        onSubmit={(title, description) =>
          presetState.createPreset(
            title,
            description,
            getPresetStates(layout.columnStates),
            gridSettings
          )
        }
        data-testid={suffixTestId('saveAsView', {'data-testid': testId})}
      />,
      {
        id: 'createPreset',
        title: t('general.presets.createPreset'),
        'data-testid': suffixTestId('saveAsView', {'data-testid': testId}),
        size: 'small',
        scrollBehavior: 'outside',
      }
    );

  const {handleToggleSelection, handleDeselectAll, selectedRowsActions} = useGridSelection({
    store,
    rowSelectMode,
    selectedRowIds,
    rows,
    onRowSelectionChange,
  });

  useGridDataNotification({
    rows,
    fetchState,
    actionsDefinition: actionsDefinition ?? EMPTY_ACTIONS_DEFINITION,
    onGridDataChange: props.onGridDataChange,
  });

  useExternalBulkActionInvocation({
    gridCode,
    rows,
    actionCallback,
    refreshData: props.refreshData,
  });

  const handleRowClick = (row: RowData, event: React.MouseEvent | React.PointerEvent) => {
    // Row clicks do not toggle selection — selection is driven exclusively by the
    // RowSelectCheckbox. Clicking a row only invokes the row's configured rowClick action.
    const rowClickActionKey = row.actions.rowClick ?? null;

    if (
      rowClickActionKey &&
      actionCallback &&
      actionsDefinition &&
      getSafely(actionsDefinition, rowClickActionKey)
    ) {
      const result = actionCallback({
        actionKey: rowClickActionKey,
        rowId: row.id,
        getColumnData: getColumnData(row, gridCode),
        rowData: row,
        refreshData: props.refreshData,
        refreshCells: () => undefined,
        redrawRows: () => undefined,
        deselectAll: () => store.deselectAll(),
        sourceSearchParams: setSourceSearchParamsByGridCode(gridCode, String(row.rowNumber)),
        queryId: props.queryIdRef.current ?? '',
        // Row click does not participate in action locking — no-op control
        actionLock: {lock: () => undefined, unlock: () => undefined},
      });

      if (typeof result === 'string') {
        handleLink(result, event.nativeEvent as MouseEvent);
      }
    }
  };

  // Recovers a row click the browser drops when the grid relayouts between the press
  // and the release (filter bar appearing, skeletons swapping for rows) — see the hook.
  const clickableRows =
    isLoading && hasSilentRefetching && previousRows.length > 0 ? previousRows : tableRows;
  const rowClickFallback = useRowClickFallback({rows: clickableRows, onRowClick: handleRowClick});

  // handleActionClick: fires when an action button in RowActions is clicked.
  // useRowActionClick manages per-action locking (Promise-based + manual dialog pattern).
  const handleActionClick = useRowActionClick(
    actionCallback,
    gridCode,
    props.queryIdRef as React.RefObject<string>,
    props.refreshData
  );

  const contextMenuEnabled = useContextMenuEnabled();
  const clipboardExportAllowed = useClipboardExportAllowed();

  const handleRowContextMenu = useRowContextMenu({
    store,
    columns: layout.visibleColumns,
    rows,
    actionsDefinition,
    clipboardEnabled: clipboardExportAllowed,
    filterByValueEnabled: props.filterByValueEnabled ?? false,
    onActionClick: handleActionClick,
    onFilterChange: handleFilterChange,
  });

  const visibleColumnKeys = useMemo(
    () => layout.visibleColumns.map((c) => c.key),
    [layout.visibleColumns]
  );
  const {handleMouseDown: handleRangeMouseDown} = useColumnSelection({
    store,
    enabled: showStatusBar,
    visibleColumnKeys,
    instanceId: gridCode,
  });

  const dragOverlay = useDragOverlay(
    activeDragId,
    layout.visibleColumnMap,
    layout.visibleColumnStateMap,
    groupRowModel,
    layout.columnWidthMap
  );

  /**
   * @mandatory Context.Provider value must be a stable reference: a fresh object
   * literal on every render would re-render every ColumnMenuContext consumer
   * regardless of whether any field actually changed. React Compiler does not
   * guarantee reference stability for Provider value objects, so an explicit
   * useMemo is required here (one of the documented exceptions in rules/code-quality.md).
   */
  const columnMenuContextValue = useMemo<ColumnMenuContextValue>(
    () => ({
      showColumnMenu: columnMenuEnabled,
      showPinning: columnPinningAllowed,
      showAutosize: columnAutoSizingAllowed,
      filterState: filterValues ?? {},
      columnStates: layout.columnStateMap,
      onFilterChange: handleFilterChange,
      onPinColumn: handlePinColumn,
      onAutosizeColumn: handleAutosizeColumn,
      onAutosizeAllColumns: handleAutosizeAllColumns,
      onResetColumns: props.handleResetColumns,
      onResetFilters: handleResetFilters,
      onSort: handleSortDirect,
      menuBoundary,
    }),
    [
      columnMenuEnabled,
      columnPinningAllowed,
      columnAutoSizingAllowed,
      filterValues,
      layout.columnStateMap,
      handleFilterChange,
      handlePinColumn,
      handleAutosizeColumn,
      handleAutosizeAllColumns,
      props.handleResetColumns,
      handleResetFilters,
      handleSortDirect,
      menuBoundary,
    ]
  );

  // Extracted to avoid duplicating the scroll container + table structure between the
  // DndContext (columnMovingAllowed=true) and plain-div (columnMovingAllowed=false) branches.
  // The only difference between those branches is the outer DndContext wrapper and the DragOverlay.
  const tableScrollArea = (
    <TableScrollArea
      autoHeight={autoHeight}
      isColumnsReady={isColumnsReady}
      gridCode={gridCode}
      hasCheckboxColumn={hasCheckboxColumn}
      actionColumnWidth={actionColumnWidth}
      rowSelectMode={rowSelectMode}
      itemsPerPage={itemsPerPage}
      headerHeightPx={HeaderHeightToPx[gridSettings.headerHeight]}
      rowHeightPx={LineHeightToPx[gridSettings.rowHeight]}
      stripedRows={gridSettings.stripedRows}
      disableAllActions={store.getState().disableAllActions}
      actionsAlignment={store.getState().actionsAlignment}
      isLoading={isLoading}
      hasSilentRefetching={hasSilentRefetching}
      isMobile={isMobile}
      tableRef={tableRef}
      colgroupRef={colgroupRef}
      rows={tableRows}
      previousRows={previousRows}
      selectedRowIds={selectedRowIds}
      groupRowModel={groupRowModel}
      columnMenuContextValue={columnMenuContextValue}
      visibleColumns={layout.visibleColumns}
      columnStateMap={layout.columnStateMap}
      nonRightPinnedColumns={layout.nonRightPinnedColumns}
      rightPinnedColumns={layout.rightPinnedColumns}
      nonRightPinnedColumnStates={layout.nonRightPinnedColumnStates}
      rightPinnedColumnStates={layout.rightPinnedColumnStates}
      orderedVisibleColumnStates={layout.orderedVisibleColumnStates}
      visibleColumnIndexMap={layout.visibleColumnIndexMap}
      colIndexMap={layout.colIndexMap}
      pinnedLeftOffsets={layout.pinnedLeftOffsets}
      pinnedRightOffsets={layout.pinnedRightOffsets}
      lastPinnedLeftKey={layout.lastPinnedLeftKey}
      firstPinnedRightKey={layout.firstPinnedRightKey}
      sortableIds={sortableIds}
      onSort={handleSort}
      onResizeStart={resizeManager.onResizeStart}
      showSummaryRow={showSummaryRow}
      onRowClick={handleRowClick}
      onActionClick={handleActionClick}
      onRowContextMenu={contextMenuEnabled ? handleRowContextMenu : undefined}
      onToggleSelection={handleToggleSelection}
    />
  );

  return (
    <div
      className={
        autoHeight
          ? 'flex flex-col overflow-hidden min-w-0 relative'
          : 'flex flex-col h-full overflow-hidden min-w-0 relative'
      }
    >
      <GridHeader
        definition={definition}
        dataGridSettings={gridSettings}
        dataGridProps={{gridCode}}
        columnStates={layout.columnStates}
        refreshData={props.refreshData}
        dataQuery={dataQuery}
        activePanel={activePanel}
        onTogglePanel={handleTogglePanel}
        activeFilterCount={activeFilterCount}
        autoFocusSmartSearch={props.autoFocusSmartSearch}
        maximizationEnabled={props.maximization?.enabled}
        tableRef={tableRef}
        data-testid={testId}
      />
      {/* "Filtered by" bar — user-toggleable via Settings (undefined = visible). */}
      <Show when={gridSettings.activeFilterBarVisible !== false}>
        <ActiveFilterBar
          filterValues={filterValues}
          columnConfig={columnConfig}
          suggestionLabels={suggestionLabels}
          staticFilterKeys={store.getState().staticFilters}
          onRemoveFilter={(key) => handleFilterChange(key, undefined)}
          onClearAll={handleResetFilters}
          onSaveAsView={presetsEnabled ? handleSaveAsView : undefined}
          data-testid={testId}
        />
      </Show>
      {/* Hide selection actions when the fetch-error state drops the rows. */}
      <Show when={!isErrorState && !hasBulkActions}>
        <BulkActionSection
          selectedRowIds={selectedRowIds}
          selectedRowsActions={selectedRowsActions}
          rows={rows}
          gridCode={gridCode}
          actionCallback={actionCallback}
          refreshData={props.refreshData}
          onDeselectAll={handleDeselectAll}
        />
      </Show>

      <div
        ref={(node) => {
          containerRef(node);
          setMenuBoundary(node);
        }}
        className={
          autoHeight
            ? 'flex flex-row overflow-hidden relative min-w-0'
            : 'flex flex-row flex-1 overflow-hidden relative min-w-0'
        }
      >
        <div
          className={
            autoHeight
              ? 'flex flex-col overflow-hidden relative min-w-0 isolate'
              : 'flex flex-col flex-1 overflow-hidden relative min-w-0 isolate'
          }
          onClick={handleTableAreaClick}
          onClickCapture={rowClickFallback.handleClickCapture}
          onPointerDownCapture={rowClickFallback.handlePointerDownCapture}
          onPointerUpCapture={rowClickFallback.handlePointerUpCapture}
          onPointerCancelCapture={rowClickFallback.handlePointerCancelCapture}
          onMouseDownCapture={showStatusBar ? handleRangeMouseDown : undefined}
        >
          <ConditionalDndWrapper
            columnMovingAllowed={columnMovingAllowed}
            dnd={dnd}
            dragOverlay={dragOverlay}
          >
            {tableScrollArea}
          </ConditionalDndWrapper>
          {isEmptyState &&
            (autoHeight ? (
              // autoHeight grids size themselves from their content, so an
              // absolutely-positioned overlay contributes no height and the
              // wrapper's overflow-hidden clips it to the bare header strip
              // (T20-137497) — render in-flow so the empty state IS the content.
              <div className="flex items-center justify-center">{emptyStateNode}</div>
            ) : (
              <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                <div className="pointer-events-auto">{emptyStateNode}</div>
              </div>
            ))}
          {isErrorState &&
            (autoHeight ? (
              // Same hazard as the empty state above (T20-137497): autoHeight
              // grids size themselves from content, so an absolutely-positioned
              // overlay contributes no height and overflow-hidden clips the
              // error message AND the retry button to the bare header strip —
              // render in-flow so the error state IS the content.
              <div className="flex items-center justify-center bg-white">
                <DataStatus
                  isError
                  errorMessage={t('general.notifications.defaultErrorMessage')}
                  errorAction={{title: t('general.actions.retry'), onClick: props.retryFetch}}
                  data-testid={suffixTestId('fetchError', {'data-testid': testId})}
                />
              </div>
            ) : (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
                <DataStatus
                  isError
                  errorMessage={t('general.notifications.defaultErrorMessage')}
                  errorAction={{title: t('general.actions.retry'), onClick: props.retryFetch}}
                  data-testid={suffixTestId('fetchError', {'data-testid': testId})}
                />
              </div>
            ))}
        </div>
        <Sidebar
          columns={layout.flatColumns}
          columnConfig={columnConfig}
          filterState={filterValues}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          settings={gridSettings}
          onSettingsChange={store.setGridSettings}
          columnSortMode={props.definition.behavior?.columnSortMode}
          presetDisplayMode={presetsEnabled ? presetDisplayMode : undefined}
          onPresetDisplayModeChange={presetsEnabled ? store.setPresetDisplayMode : undefined}
          filterComponent={
            // When externalFilterId is set, the filterComponent renders via portal (ExternalFiltersPortal below).
            // Pass undefined here to prevent duplicate rendering in the sidebar.
            gridContext.externalFilterId ? undefined : gridContext.filterComponent
          }
          activePanel={activePanel}
          onClose={handleClosePanel}
          isCompact={isCompactSidebar}
          summaryRowEnabled={summaryRowEnabled}
          tableRef={tableRef}
          columnsPanelScrollToEnabled={props.columnsPanelScrollToEnabled}
        />
      </div>
      <Show when={showStatusBar}>
        <AggregationStatusBar visibleColumns={layout.visibleColumns} />
      </Show>
      <GridBulkOperations
        onRefresh={props.refreshData}
        pagination={
          paginationEnabled && totalCount > 0
            ? {
                currentPage: page,
                totalPages: Math.ceil(totalCount / itemsPerPage),
                totalCount,
                itemsPerPage,
                paginationDisplayMode: gridSettings.paginationDisplayMode,
                onPageChange: handlePageChange,
                isInlineDesktop,
                'data-testid': testId,
              }
            : undefined
        }
        data-testid={testId}
      />
      {/* ExternalFiltersPortal renders filterComponent into an external DOM element via React portal.
          Renders outside the table DOM tree but inside the store provider scope.
          Mutual exclusion: only renders when externalFilterId is set — sidebar uses native FilterPanel in that case. */}
      <Show
        when={isNotNilOrEmpty(gridContext.externalFilterId) ? gridContext.externalFilterId : null}
      >
        {(externalFilterId) => (
          <ExternalFiltersPortal
            externalFilterId={externalFilterId}
            filterComponent={gridContext.filterComponent}
            externalFilterResourceId={gridContext.externalFilterResourceId}
            externalFilterRecordId={gridContext.externalFilterRecordId}
          />
        )}
      </Show>
    </div>
  );
}
